import { Box, Paper, Typography, Divider } from "@mui/material";
import type { SiteData, MatchupRecord } from "../types";
import { getBracketLayout, type VisualColumn } from "../engine/bracketLayout";

interface BracketDisplayProps {
  siteData: SiteData;
}

interface MatchupSlot {
  seed1: number | null;
  seed2: number | null;
  winner: number | null;
  isActive: boolean;
  round: number | null;
}

function buildMatchupMap(history: MatchupRecord[]): Map<number, MatchupRecord> {
  const map = new Map<number, MatchupRecord>();
  for (const m of history) {
    map.set(m.round, m);
  }
  return map;
}

function getSlotsForColumn(
  column: VisualColumn,
  matchupMap: Map<number, MatchupRecord>,
  currentRound: number,
  current2Seeds: number[],
  seedList: number[],
  isFirstWinnersColumn: boolean,
): MatchupSlot[] {
  const slots: MatchupSlot[] = [];

  for (let i = 0; i < column.matchupCount; i++) {
    const round = column.rounds[i]!;
    const historyEntry = matchupMap.get(round);
    const isActive = round === currentRound && current2Seeds.length === 2;

    if (historyEntry) {
      slots.push({
        seed1: historyEntry.seed1,
        seed2: historyEntry.seed2,
        winner: historyEntry.winner,
        isActive: false,
        round,
      });
    } else if (isActive) {
      slots.push({
        seed1: current2Seeds[0]!,
        seed2: current2Seeds[1]!,
        winner: null,
        isActive: true,
        round,
      });
    } else if (isFirstWinnersColumn) {
      // Initial seed pairings from seed list
      const s1 = seedList[i * 2];
      const s2 = seedList[i * 2 + 1];
      slots.push({
        seed1: s1 ?? null,
        seed2: s2 ?? null,
        winner: null,
        isActive: false,
        round,
      });
    } else {
      slots.push({
        seed1: null,
        seed2: null,
        winner: null,
        isActive: false,
        round,
      });
    }
  }

  return slots;
}

function SongSlot({
  seed,
  songs,
  isWinner,
  isLoser,
}: {
  seed: number | null;
  songs: SiteData["gameSessionSongs"];
  isWinner: boolean;
  isLoser: boolean;
}) {
  if (seed === null) {
    return (
      <Box
        sx={{
          px: 0.75,
          py: 0.25,
          fontSize: "0.7rem",
          color: "text.disabled",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minHeight: 20,
        }}
      >
        TBD
      </Box>
    );
  }

  const song = songs[seed - 1];
  const name = song?.SongName || `Song ${seed}`;
  const truncated = name.length > 18 ? name.slice(0, 18) + "..." : name;

  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.25,
        fontSize: "0.7rem",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        bgcolor: isWinner ? "rgba(46, 204, 113, 0.2)" : "transparent",
        color: isLoser ? "text.disabled" : "text.primary",
        fontWeight: isWinner ? "bold" : "normal",
        minHeight: 20,
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box
        component="span"
        sx={{ color: "text.secondary", mr: 0.5, fontSize: "0.65rem" }}
      >
        ({seed})
      </Box>
      {truncated}
    </Box>
  );
}

function MatchupCell({
  slot,
  songs,
  isLastColumn,
}: {
  slot: MatchupSlot;
  songs: SiteData["gameSessionSongs"];
  isLastColumn: boolean;
}) {
  return (
    <Box
      sx={{
        position: "relative",
        "&::after": isLastColumn
          ? undefined
          : {
              content: '""',
              position: "absolute",
              right: -12,
              top: "50%",
              width: 12,
              borderTop: "1px solid rgba(255,255,255,0.15)",
            },
      }}
    >
      <Paper
        elevation={slot.isActive ? 4 : 1}
        sx={{
          width: 140,
          border: slot.isActive
            ? "2px solid #1abc9c"
            : "1px solid rgba(255,255,255,0.08)",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: slot.isActive
            ? "rgba(26, 188, 156, 0.08)"
            : "background.paper",
        }}
      >
        <SongSlot
          seed={slot.seed1}
          songs={songs}
          isWinner={slot.winner !== null && slot.winner === slot.seed1}
          isLoser={slot.winner !== null && slot.winner !== slot.seed1}
        />
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
        <SongSlot
          seed={slot.seed2}
          songs={songs}
          isWinner={slot.winner !== null && slot.winner === slot.seed2}
          isLoser={slot.winner !== null && slot.winner !== slot.seed2}
        />
      </Paper>
    </Box>
  );
}

function BracketColumn({
  column,
  slots,
  songs,
  isLastColumn,
}: {
  column: VisualColumn;
  slots: MatchupSlot[];
  songs: SiteData["gameSessionSongs"];
  isLastColumn: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: 160,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          mb: 1,
          color: "text.secondary",
          fontSize: "0.65rem",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          whiteSpace: "nowrap",
        }}
      >
        {column.label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-around",
          flex: 1,
          gap: 1,
          width: "100%",
          alignItems: "center",
        }}
      >
        {slots.map((slot, idx) => (
          <MatchupCell
            key={idx}
            slot={slot}
            songs={songs}
            isLastColumn={isLastColumn}
          />
        ))}
      </Box>
    </Box>
  );
}

function BracketTree({
  columns,
  siteData,
  matchupMap,
  label,
}: {
  columns: VisualColumn[];
  siteData: SiteData;
  matchupMap: Map<number, MatchupRecord>;
  label?: string;
}) {
  const { currentRound, current2Seeds } = siteData.bracketsModeSettings;
  const seedList = siteData.bracketsModeSettings.gameSeedSetup.seedList;
  const songs = siteData.gameSessionSongs;

  if (columns.length === 0) return null;

  // Calculate min height based on max matchup count
  const maxMatchups = Math.max(...columns.map((c) => c.matchupCount));
  const minHeight = Math.max(maxMatchups * 50, 100);

  return (
    <Box>
      {label && (
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, color: "text.secondary", fontWeight: "bold" }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          gap: "12px",
          minHeight,
          alignItems: "stretch",
        }}
      >
        {columns.map((col, colIdx) => {
          const isFirstWinnersCol =
            colIdx === 0 && col.bracket === "winners";
          const slots = getSlotsForColumn(
            col,
            matchupMap,
            currentRound,
            current2Seeds,
            seedList,
            isFirstWinnersCol,
          );
          return (
            <BracketColumn
              key={colIdx}
              column={col}
              slots={slots}
              songs={songs}
              isLastColumn={colIdx === columns.length - 1}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default function BracketDisplay({ siteData }: BracketDisplayProps) {
  const { bracketSize, gameType } = siteData;
  const { matchupHistory, currentRound, current2Seeds } =
    siteData.bracketsModeSettings;
  const seedList = siteData.bracketsModeSettings.gameSeedSetup.seedList;
  const songs = siteData.gameSessionSongs;

  const layout = getBracketLayout(bracketSize, gameType);
  const matchupMap = buildMatchupMap(matchupHistory || []);

  if (gameType === "SingleE") {
    return (
      <Box id="bracket-display" sx={{ overflowX: "auto", pb: 2, mb: 3 }}>
        <BracketTree
          columns={layout.winnersColumns}
          siteData={siteData}
          matchupMap={matchupMap}
        />
      </Box>
    );
  }

  // Double elimination
  return (
    <Box id="bracket-display" sx={{ overflowX: "auto", pb: 2, mb: 3 }}>
      <BracketTree
        columns={layout.winnersColumns}
        siteData={siteData}
        matchupMap={matchupMap}
        label="Winners Bracket"
      />
      <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.1)" }} />
      <BracketTree
        columns={layout.losersColumns}
        siteData={siteData}
        matchupMap={matchupMap}
        label="Losers Bracket"
      />
      {layout.finalsColumn && (
        <>
          <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.1)" }} />
          <Box id="bracket-grand-finals">
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, color: "text.secondary", fontWeight: "bold" }}
            >
              Grand Finals
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {(() => {
                const col = layout.finalsColumn;
                const slots = getSlotsForColumn(
                  col,
                  matchupMap,
                  currentRound,
                  current2Seeds,
                  seedList,
                  false,
                );
                return (
                  <BracketColumn
                    column={col}
                    slots={slots}
                    songs={songs}
                    isLastColumn={true}
                  />
                );
              })()}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
